import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

export const IntlPropType = PropTypes.shape({
  formatDate: PropTypes.func.isRequired,
  formatTime: PropTypes.func.isRequired,
  formatRelativeTime: PropTypes.func.isRequired,
  formatNumber: PropTypes.func.isRequired,
  formatPlural: PropTypes.func.isRequired,
  formatMessage: PropTypes.func.isRequired,
});

/**
 * @typedef {object} IntlPropType
 * @property {Function} formatDate
 * @property {Function} formatTime
 * @property {Function} formatRelativeTime
 * @property {Function} formatNumber
 * @property {Function} formatPlural
 * @property {Function} formatMessage
 */

export const MessageDescriptorPropType = PropTypes.shape({
  id: PropTypes.string,
  defaultMessage: PropTypes.string,
  description: PropTypes.string,
});

/**
 * @typedef {object} MessageDescriptorPropType
 * @property {string} id
 * @property {string} defaultMessage
 * @property {string} description
 */

export const SharedMessagesPropType = PropTypes.shape({
  buttonContent: MessageDescriptorPropType,
  actionDescription: MessageDescriptorPropType,
});

/**
 * @typedef {object} SharedMessagesPropType
 * @property {MessageDescriptorPropType} buttonContent
 * @property {MessageDescriptorPropType} actionDescription
 */

// FormattedMessage
// oxlint-disable-next-line @typescript-eslint/no-unsafe-member-access, formatjs/enforce-default-message, formatjs/enforce-description, formatjs/enforce-id
const FormattedMessageType = (<FormattedMessage />).type;
const FormattedMessageValidator = (isRequired) => (props, propName, componentName) => {
  const formattedMessageProp = props[propName];
  if (!formattedMessageProp && isRequired) {
    return new Error(`Invalid prop: No ${propName} supplied to ${componentName}. ${propName} is marked as required`);
  }

  if (formattedMessageProp && formattedMessageProp.type !== FormattedMessageType) {
    return new Error(`Invalid prop: ${propName} supplied to ${componentName} is not a <FormattedMessage /> type`);
  }

  const fmProps = formattedMessageProp?.props || {};
  const keys = Object.keys(fmProps);
  const hasIdString = keys.includes('id') || typeof fmProps.id !== 'string';
  const hasDefaultMessageString = keys.includes('defaultMessage') || typeof fmProps.defaultMessage !== 'string';

  if (!hasIdString || !hasDefaultMessageString) {
    return new Error(`${propName} expects id and defaultMessage props.`);
  }

  return null;
};

export const FormattedMessagePropType = FormattedMessageValidator(false);
FormattedMessagePropType.isRequired = FormattedMessageValidator(true);
FormattedMessagePropType.type = 'FormattedMessagePropType';
FormattedMessagePropType.isRequired.type = 'FormattedMessagePropType';

// FormattedNumber
const FormattedNumberType = (<FormattedNumber />).type;
const FormattedNumberValidator = (isRequired) => (props, propName, componentName) => {
  const formattedNumberProp = props[propName];

  if (!formattedNumberProp && isRequired) {
    return new Error(`Invalid prop: No ${propName} supplied to ${componentName}. ${propName} is marked as required`);
  }

  if (formattedNumberProp && formattedNumberProp.type !== FormattedNumberType) {
    return new Error(`Invalid prop: ${propName} supplied to ${componentName} is not a <FormattedNumber /> type`);
  }

  return null;
};
export const FormattedNumberPropType = FormattedNumberValidator(false);
FormattedNumberPropType.isRequired = FormattedNumberValidator(true);
